import { ChevronLeft, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import EditCoursePanel from '@/components/CreateNewCoursePanel/CreateNewCoursePanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGetAdminPanelCourses } from '@/hooks/queries/useAdminPanelCourses';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetDivisions } from '@/hooks/queries/useDivisions';
import type { CourseWithRelations, DisciplineWithCourses } from '@/lib/types';

import { isSpacerMapNode, SPACER_COURSE_NAME } from './course-utils';
import type { NodeEntity } from './coursemap.types';

const SPACER_OPTION = {
	course_id: 'spacer',
	course_name: SPACER_COURSE_NAME,
} as CourseWithRelations;

interface AddCoursePopoverProps {
	/** The node whose + button was clicked — used only for depth check. */
	node: NodeEntity;
	/** Pre-computed screen-space position (fixed coords). */
	screenX: number;
	screenY: number;
	/** The hierarchy node that will become the parent of the new child. */
	selectedNode: NodeEntity | undefined;
	/** Placeholder hierarchy object that gets mutated and pushed into the tree. */
	newCourse: any;
	/** Root tree data object — passed straight through to updateTreeData. */
	treeData: any;
	updateTreeData: (obj: any, label?: string) => void;
	onClose: () => void;
}

const POPOVER_WIDTH = 320;

const AddCoursePopover = ({
	node,
	screenX,
	screenY,
	selectedNode,
	newCourse,
	treeData,
	updateTreeData,
	onClose,
}: AddCoursePopoverProps) => {
	const [search, setSearch] = useState('');
	const [showCreateForm, setShowCreateForm] = useState(false);
	const popoverRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	const { data: coursesList = [], isLoading } = useGetAdminPanelCourses();
	const { data: disciplines = [] } = useGetDisciplines();
	const { data: divisions = [] } = useGetDivisions();

	const isDisciplineLevel = node.depth === 0;

	// Auto-focus search field
	useEffect(() => {
		if (!isDisciplineLevel) searchInputRef.current?.focus();
	}, [isDisciplineLevel]);

	// Close on outside pointer-down
	useEffect(() => {
		const handlePointerDown = (e: PointerEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [onClose]);

	// Close on Escape
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	function isDiscipline(item: any): item is DisciplineWithCourses {
		return Boolean((item as DisciplineWithCourses).color);
	}

	const chooseOption = useCallback(
		(item: CourseWithRelations | DisciplineWithCourses) => {
			if (!selectedNode || !newCourse) return;

			const kind = isDiscipline(item) ? 'Discipline' : 'Course';

			if (isDiscipline(item)) {
				newCourse.id = item.id;
				newCourse.tracker = item.id;
				newCourse.data.name = item.name;
				newCourse.data.id = item.id;
				newCourse.data.tracker = item.id;
				newCourse.data.division = '';
				newCourse.data.discipline = item.name;
				newCourse.data.color = item.color;
				newCourse.data.isDiscipline = true;
			} else {
				if ((item as CourseWithRelations).is_archived) return;
				const isSpacer = isSpacerMapNode(item.course_name);
				const id = isSpacer ? crypto.randomUUID() : item.course_id;
				newCourse.id = id;
				newCourse.tracker = id;
				newCourse.data.name = isSpacer ? SPACER_COURSE_NAME : item.course_name;
				newCourse.data.id = id;
				newCourse.data.tracker = id;
				newCourse.data.division = isSpacer ? '' : item.courses_division?.division?.name;
				newCourse.data.discipline = isSpacer ? '' : item.courses_discipline?.discipline?.name;
			}

			if (selectedNode.children) {
				selectedNode.children.push(newCourse);
			} else {
				selectedNode.children = [newCourse];
			}
			if (selectedNode.data.children) {
				selectedNode.data.children.push(newCourse.data);
			} else {
				selectedNode.data.children = [newCourse.data];
			}

			updateTreeData(treeData, `Added ${kind} '${newCourse.data.name}'`);
			onClose();
		},
		[selectedNode, newCourse, treeData, updateTreeData, onClose]
	);

	// Filtered list
	const searchValue = search.toLowerCase();
	const filteredCourses = (coursesList as CourseWithRelations[]).filter(
		(c) => !c.is_archived && c.course_name?.toLowerCase().includes(searchValue)
	);
	const spacerMatches = SPACER_COURSE_NAME.toLowerCase().includes(searchValue);

	// Clamp to viewport
	const vpWidth = window.innerWidth;
	const vpHeight = window.innerHeight;
	const left = Math.min(screenX, vpWidth - POPOVER_WIDTH - 16);
	const top = Math.min(Math.max(screenY, 8), vpHeight - 460);

	return (
		<div
			ref={popoverRef}
			style={{
				position: 'fixed',
				left,
				top,
				width: POPOVER_WIDTH,
				zIndex: 1000,
			}}
			className="flex flex-col overflow-hidden rounded-xl border border-border bg-(--panel-color) shadow-2xl"
		>
			{/* ── Header ─────────────────────────────────────────────────────── */}
			<div className="flex items-center justify-between border-b px-3 py-2.5">
				{showCreateForm ? (
					<button
						type="button"
						onClick={() => setShowCreateForm(false)}
						className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
					>
						<ChevronLeft size={15} />
						Back
					</button>
				) : (
					<span className="text-sm font-semibold">
						{isDisciplineLevel ? 'Add Discipline' : 'Add Course'}
					</span>
				)}
				<button
					type="button"
					onClick={onClose}
					className="rounded-md p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
					aria-label="Close"
				>
					<X size={15} />
				</button>
			</div>

			{/* ── Search (courses only, when not in create-form view) ─────────── */}
			{!isDisciplineLevel && !showCreateForm && (
				<div className="px-3 pt-2 pb-1">
					<div className="relative">
						<Search
							size={13}
							className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
						/>
						<Input
							ref={searchInputRef}
							placeholder="Search courses…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="h-8 pl-7 text-sm"
						/>
					</div>
				</div>
			)}

			{/* ── Create-new course form ──────────────────────────────────────── */}
			{showCreateForm ? (
				<div className="flex-1 overflow-y-auto px-3 py-3" style={{ maxHeight: 400 }}>
					<EditCoursePanel
						disciplines={disciplines as any}
						divisions={divisions as any}
						closeAccordion={() => setShowCreateForm(false)}
						onCourseCreated={(course) => {
							chooseOption(course as any);
						}}
					/>
				</div>
			) : (
				<>
					{/* ── List ───────────────────────────────────────────────────── */}
					<div className="flex-1 overflow-y-auto" style={{ maxHeight: 320 }}>
						{isLoading && (
							<p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
						)}

						{/* Discipline items (depth 0) */}
						{!isLoading &&
							isDisciplineLevel &&
							(disciplines as DisciplineWithCourses[]).map((d) => (
								<button
									type="button"
									key={d.id}
									onClick={() => chooseOption(d)}
									className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-accent transition-colors"
								>
									{d.color && (
										<span
											className="inline-block h-3 w-3 shrink-0 rounded-full"
											style={{ backgroundColor: d.color }}
										/>
									)}
									<span className="text-sm font-medium">{d.name}</span>
								</button>
							))}

						{/* Course items (depth > 0) */}
						{!isLoading && !isDisciplineLevel && !spacerMatches && filteredCourses.length === 0 && (
							<p className="py-8 text-center text-sm text-muted-foreground">No matches</p>
						)}
						{!isLoading && !isDisciplineLevel && spacerMatches && (
							<button
								type="button"
								onClick={() => chooseOption(SPACER_OPTION)}
								className="flex w-full flex-col gap-0.5 border-b border-dashed px-3 py-2.5 text-left hover:bg-accent transition-colors"
							>
								<span className="text-sm font-medium leading-snug">Spacer</span>
								<span className="text-xs text-muted-foreground leading-tight">Layout only</span>
							</button>
						)}
						{!isLoading &&
							!isDisciplineLevel &&
							filteredCourses.map((c) => {
								const discipline = c.courses_discipline?.discipline?.name;
								const division = c.courses_division?.division?.name;
								const sub = [discipline, division].filter(Boolean).join(' · ');

								return (
									<button
										type="button"
										key={c.course_id}
										onClick={() => chooseOption(c)}
										className="flex w-full flex-col gap-0.5 px-3 py-2.5 text-left hover:bg-accent transition-colors"
									>
										<span className="text-sm font-medium leading-snug">{c.course_name}</span>
										{sub && (
											<span className="text-xs text-muted-foreground leading-tight">{sub}</span>
										)}
									</button>
								);
							})}
					</div>

					{/* ── Footer: create-new ─────────────────────────────────────── */}
					{!isDisciplineLevel && (
						<div className="border-t px-3 py-2">
							<Button
								variant="ghost"
								size="small"
								className="w-full justify-start gap-2 text-primary hover:text-primary hover:bg-primary/8 font-medium"
								onClick={() => setShowCreateForm(true)}
							>
								<Plus size={14} />
								Create new course
							</Button>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default AddCoursePopover;
